import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { DrmScheme, MediaType, Player, PlaybackOptions } from '@quickplay/player-interface';
import { ExternalPlayer } from '../ExternalPlayer';
import { PlayerBuilder } from '../PlayerBuilder';
import { createCorePlayer } from './CorePlayer';
import SceUvp from './UVPPlayer/internal/SceUvp';
import { ThumbnailPreviewConfig } from '../thumbnailPreview/ThumbnailPreviewConfiguration';

const TAG_NAME = 'FL_PLAYER';

export interface UVPPlayerBuilder extends PlayerBuilder {
  readonly sceUvp: typeof SceUvp;
  readonly videoWrapper: HTMLElement;

  /**
   * @method mediaWrapperElement
   * @param element HTMLElement
   *
   * The wrapper element for video element.
   */
  mediaWrapperElement(element: HTMLElement): PlayerBuilder;

  /**
   * @method usePlayer
   * @param externalPlayer
   * @param SceUvp The instance of the UVP Player.
   * This argument is only used if the first argument the `externalPlayer` is `ExternalPlayer.UVPPlayer`.
   *
   * By default shakaplayer is used.
   */
  usePlayer(externalPlayer: ExternalPlayer, sceUvp: typeof SceUvp): PlayerBuilder;
}

export function CoreUVPPlayerBuilder(): UVPPlayerBuilder {
  let contentUrl!: string;
  let mediaFormatType: MediaType = MediaType.DASH;
  let licenseUrl = '';
  let drmType: DrmScheme = DrmScheme.NONE;
  const options: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    enableEngagementScore: false,
  };
  let videoWrapperElement: HTMLElement;
  let extPlayer: ExternalPlayer;
  let serverCertificate: ArrayBuffer;
  let sideloadedtracks: any[];
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let sceUvp: typeof SceUvp;
  let config: ThumbnailPreviewConfig;
  let thumbnailPreviewEndpointUrl: string | undefined;
  let webVTTThumbnailPreviewEndpointUrl: string | undefined;
  let library: any;

  return {
    get contentUrl(): string {
      return contentUrl;
    },
    get extPlayer(): any {
      return extPlayer || ExternalPlayer.ShakaPlayer;
    },

    get mediaFormatType(): MediaType {
      return mediaFormatType || MediaType.DASH;
    },

    get licenseUrl(): string {
      return licenseUrl || '';
    },

    get drmType(): DrmScheme {
      return drmType || DrmScheme.PLAYREADY;
    },

    get options(): PlaybackOptions {
      return options;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get videoWrapper(): HTMLElement {
      return videoWrapperElement;
    },

    get serverCertificate(): ArrayBuffer {
      return serverCertificate;
    },

    get getSideLoadedTracks(): any[] {
      return sideloadedtracks;
    },

    get config(): ThumbnailPreviewConfig {
      return config;
    },

    get thumbnailPreviewEndpointUrl(): string | undefined {
      return thumbnailPreviewEndpointUrl;
    },

    get webVTTThumbnailPreviewEndpointUrl(): string | undefined {
      return webVTTThumbnailPreviewEndpointUrl;
    },

    get sceUvp(): typeof SceUvp {
      return sceUvp;
    },

    get library(): any {
      return library;
    },

    mediaElement(_: HTMLVideoElement): PlayerBuilder {
      return this;
    },

    mediaWrapperElement(element: HTMLElement): PlayerBuilder {
      videoWrapperElement = element;
      return this;
    },

    mediaUrl(mediaUrl: string): PlayerBuilder {
      contentUrl = encodeURI(mediaUrl.trim());
      return this;
    },

    mediaType(type: MediaType): PlayerBuilder {
      mediaFormatType = type;
      return this;
    },

    drmLicenseUrl(drmLicenseUrl: string): PlayerBuilder {
      licenseUrl = drmLicenseUrl;
      return this;
    },

    drmScheme(drmScheme: DrmScheme): PlayerBuilder {
      drmType = drmScheme;
      return this;
    },

    playbackOptions(playbackOptions: PlaybackOptions): PlayerBuilder {
      // Note: UVP player does not like if we orchestrate autoplay via the StateMachine
      // esp., for live playback, hence we let the underlying player manage autoplay.
      Object.assign(options, Object.assign(playbackOptions, { autoPlayManagedByPlatform: true }));
      return this;
    },

    usePlayer(externalPlayer: ExternalPlayer, sceUvpLib: typeof SceUvp): PlayerBuilder {
      extPlayer = externalPlayer;
      sceUvp = sceUvpLib;
      return this;
    },

    certificate(certificate: ArrayBuffer): PlayerBuilder {
      serverCertificate = certificate;
      return this;
    },

    sideCarTracks(vttTracks: any[]): PlayerBuilder {
      sideloadedtracks = vttTracks;
      return this;
    },

    logger(logger: Logger): PlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    enableThumbnailPreview(thumbnailPreviewEndpointUrl_: string, config_: ThumbnailPreviewConfig = {}): PlayerBuilder {
      thumbnailPreviewEndpointUrl = thumbnailPreviewEndpointUrl_;
      config = config_;
      return this;
    },

    webVTTThumbnailPreview(webVTTTrackUrl: string): PlayerBuilder {
      webVTTThumbnailPreviewEndpointUrl = webVTTTrackUrl;
      return this;
    },

    playerLibrary(playerLib: any): PlayerBuilder {
      library = playerLib;
      return this;
    },

    build(): Player {
      return createCorePlayer(this);
    },
  };
}
